import React from 'react';
import { Grid, Typography } from '@mui/material';
import SparkRunsHeader from './SparkRunsHeader';
import SparkRunsContent from './SparkRunsContent';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SparkRunByIDSkeleton from 'sharedComponents/Skeleton/SparkRunByIDSkeleton';
import { useStatusBarStore } from 'stores';
import {
  SparkButton,
  SparkImageLoader,
  SparkScrollableGridContainer,
} from 'sharedComponents/UI';
import { useSparksByID } from 'hooks/spark';
import useExpandAppContentConfig from 'utilities/styles';
import { useSparkStatusType } from 'hooks/EntityTypes/useSparkStatusType';
import SparkRunDraftMessage from './SparkRunDraftMessage';

function SparkRuns() {
  const { id: sparkID } = useParams();
  const [searchParams] = useSearchParams();
  const access_token = searchParams.get('access_token');
  const navigate = useNavigate();
  const { expandAppClasses, stylesImplemented } = useExpandAppContentConfig();

  const statusBarHeight = useStatusBarStore((state) => state.statusBarHeight);

  const isRunning = useStatusBarStore((state) => state.isRunning);
  const {
    joinedSpark,
    isLoading: isLoadingSpark,
    permissionDetailsErrors,
  } = useSparksByID(sparkID, access_token);

  const { idByNameMap: sparkStatusTypes, isLoading: isLoadingStatusTypes } =
    useSparkStatusType();
  const sparkStatusTypeID = joinedSpark?.attributes.sparkStatusTypeID;
  const isPublished =
    sparkStatusTypeID &&
    String(sparkStatusTypeID) === sparkStatusTypes?.Published;

  if (!sparkID || isLoadingSpark || isLoadingStatusTypes) {
    return <SparkRunByIDSkeleton count={3} />;
  }

  if (permissionDetailsErrors) {
    const handleCtaLinkClick = () => {
      if (!!permissionDetailsErrors.ctaRoute) {
        navigate(permissionDetailsErrors.ctaRoute);
        return;
      }
      window.open(permissionDetailsErrors.ctaLink);
    };
    return (
      <Grid
        container
        direction='column'
        height='100vh'
        justifyContent='center'
        alignItems='center'
        gap={2}
      >
        <Grid item>
          <SparkImageLoader
            src={permissionDetailsErrors.iconURL}
            width='100px'
            height='100px'
          />
        </Grid>
        <Grid item>
          <Typography
            sx={{
              fontSize: '26px',
              fontFamily: 'Mont-semibold',
            }}
          >
            {permissionDetailsErrors.title}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant='body1'>
            {permissionDetailsErrors.description}
          </Typography>
        </Grid>
        <Grid item>
          <SparkButton variant='contained' onClick={handleCtaLinkClick}>
            {permissionDetailsErrors.ctaLabel}
          </SparkButton>
        </Grid>
      </Grid>
    );
  }

  return (
    <SparkScrollableGridContainer
      header={<SparkRunsHeader joinedSpark={joinedSpark} />}
      headerClasses={expandAppClasses.header}
      headerStyle={{
        padding: 0,
        bgcolor: 'white',
        height: '6rem',
        borderBottom: (theme) => `1px solid ${theme.palette.borderColor}`,
        transition: (theme) =>
          theme.transitions.create('height', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
      }}
      height={`calc(100vh - ${statusBarHeight}px)`}
      scrollableContainerStyle={{
        position: 'relative',
        padding: 0,
        display: 'flex',
        justifyContent: `${!isRunning ? 'left' : 'center'}`,
        alignItems: `${!isRunning ? 'center' : ''}`,
        flexDirection: 'column',
      }}
      sx={stylesImplemented}
    >
      {isPublished ? (
        <SparkRunsContent sparkID={sparkID} />
      ) : (
        <SparkRunDraftMessage />
      )}
    </SparkScrollableGridContainer>
  );
}

export default SparkRuns;
